import { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { firestoreService } from '../services/firebase'
import { roleService, PERMISSION_CATALOG } from '../services/roleService'
import { Users, Shield, Edit, Save, X, Plus, Search, Trash2 } from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'
import CustomDropdown from '../components/CustomDropdown'
import { cn } from '../utils/cn'

const permissionGroups = [...new Set(PERMISSION_CATALOG.map((p) => p.group))]

export default function RoleManagementPage() {
  const { userProfile, currentUser } = useAuth()
  const [tab, setTab] = useState('roles') // roles | users
  const [users, setUsers] = useState([])
  const [roles, setRoles] = useState([])
  const [loading, setLoading] = useState(true)
  const [editingUser, setEditingUser] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [showRoleForm, setShowRoleForm] = useState(false)
  const [editingRole, setEditingRole] = useState(null)
  const [savingRole, setSavingRole] = useState(false)

  const [roleForm, setRoleForm] = useState({
    name: '',
    description: '',
    hierarchyLevel: 2,
    permissions: [],
  })

  useEffect(() => {
    if (!roleService.canUserPerformAction(userProfile, 'manage_roles')) {
      toast.error('You do not have permission to manage roles')
      return
    }
    loadData()
  }, [userProfile])

  async function loadData() {
    setLoading(true)
    try {
      const rolesData = await roleService.ensureDefaultRoles()
      const usersData = await firestoreService.getAll('users')

      const normalizedUsers = (usersData || []).map((user) => ({
        ...user,
        permissions: Array.isArray(user.permissions) ? user.permissions : [],
      }))

      setUsers(normalizedUsers)
      setRoles(rolesData)
    } catch (error) {
      console.error('Error loading data:', error)
      toast.error('Failed to load role data')
    } finally {
      setLoading(false)
    }
  }

  function openCreateRole() {
    setEditingRole(null)
    setRoleForm({ name: '', description: '', hierarchyLevel: 2, permissions: [] })
    setShowRoleForm(true)
  }

  function openEditRole(role) {
    setEditingRole(role)
    setRoleForm({
      name: role.name || '',
      description: role.description || '',
      hierarchyLevel: Number(role.hierarchyLevel || 2),
      permissions: Array.isArray(role.permissions) ? [...role.permissions] : [],
    })
    setShowRoleForm(true)
  }

  function toggleFormPermission(permissionId) {
    setRoleForm((prev) => {
      const has = prev.permissions.includes(permissionId)
      return {
        ...prev,
        permissions: has
          ? prev.permissions.filter((p) => p !== permissionId)
          : [...prev.permissions, permissionId],
      }
    })
  }

  async function saveRole(e) {
    e.preventDefault()
    if (!roleForm.name.trim()) {
      toast.error('Role name is required')
      return
    }
    setSavingRole(true)
    try {
      if (editingRole?.id) {
        await roleService.updateRole(editingRole.id, {
          name: roleForm.name.trim(),
          description: roleForm.description.trim(),
          hierarchyLevel: Number(roleForm.hierarchyLevel || 2),
          permissions: roleForm.permissions,
        })
        toast.success('Role updated — permissions synced to all users with this role')
      } else {
        await roleService.createRole({
          name: roleForm.name.trim(),
          description: roleForm.description.trim(),
          hierarchyLevel: Number(roleForm.hierarchyLevel || 2),
          permissions: roleForm.permissions,
          isSystem: false,
        })
        toast.success('Role created')
      }
      setShowRoleForm(false)
      setEditingRole(null)
      await loadData()
    } catch (error) {
      console.error(error)
      toast.error(error?.message || 'Failed to save role')
    } finally {
      setSavingRole(false)
    }
  }

  async function deleteRole(role) {
    if (!confirm(`Delete role "${role.name}"?`)) return
    try {
      await roleService.deleteRole(role.id)
      toast.success('Role deleted')
      await loadData()
    } catch (error) {
      toast.error(error?.message || 'Failed to delete role')
    }
  }

  async function updateUserRole(userId, newRole, newPermissions) {
    try {
      await roleService.assignRoleToUser(userId, newRole, newPermissions, currentUser.uid)
      setUsers((prev) =>
        prev.map((user) =>
          user.id === userId ? { ...user, role: newRole, permissions: newPermissions } : user
        )
      )
      setEditingUser(null)
      toast.success('User role updated successfully')
      await loadData()
    } catch (error) {
      console.error('Error updating user role:', error)
      toast.error('Failed to update user role')
    }
  }

  const roleOptions = useMemo(
    () =>
      roles.map((r) => ({
        value: r.key || r.id,
        label: r.name || r.key || r.id,
      })),
    [roles]
  )

  const filteredUsers = users.filter((user) => {
    const q = searchTerm.toLowerCase()
    return (
      user.name?.toLowerCase().includes(q) ||
      user.displayName?.toLowerCase().includes(q) ||
      user.firstName?.toLowerCase().includes(q) ||
      user.email?.toLowerCase().includes(q) ||
      user.role?.toLowerCase().includes(q)
    )
  })

  if (loading) {
    return (
      <div className="min-h-96 flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Role Management</h1>
          <p className="text-gray-600">
            Create roles and choose exactly what each role can access
          </p>
        </div>
        {tab === 'roles' && (
          <button
            type="button"
            onClick={openCreateRole}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            Create role
          </button>
        )}
      </div>

      <div className="flex gap-2 border-b border-border">
        <button
          type="button"
          onClick={() => setTab('roles')}
          className={cn(
            'px-4 py-2 text-sm font-medium border-b-2 -mb-px',
            tab === 'roles'
              ? 'border-primary text-foreground'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          )}
        >
          Roles & access
        </button>
        <button
          type="button"
          onClick={() => setTab('users')}
          className={cn(
            'px-4 py-2 text-sm font-medium border-b-2 -mb-px',
            tab === 'users'
              ? 'border-primary text-foreground'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          )}
        >
          Assign to users
        </button>
      </div>

      {tab === 'roles' && (
        <div className="grid gap-4 md:grid-cols-2">
          {roles.length === 0 ? (
            <p className="text-sm text-muted-foreground md:col-span-2">No roles yet.</p>
          ) : (
            roles.map((role) => (
              <div
                key={role.id}
                className="rounded-xl border border-border/80 bg-card p-5 shadow-sm space-y-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-primary" />
                      <h3 className="font-semibold text-foreground">{role.name}</h3>
                      {role.isSystem && (
                        <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                          System
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Key: {role.key || role.id} · Level {role.hierarchyLevel ?? '—'}
                    </p>
                    {role.description && (
                      <p className="mt-2 text-sm text-muted-foreground">{role.description}</p>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => openEditRole(role)}
                      className="rounded-lg p-2 text-indigo-600 hover:bg-indigo-50"
                      title="Edit role"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    {!role.isSystem && (
                      <button
                        type="button"
                        onClick={() => deleteRole(role)}
                        className="rounded-lg p-2 text-red-600 hover:bg-red-50"
                        title="Delete role"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(role.permissions || []).length === 0 ? (
                    <span className="text-xs text-muted-foreground">No permissions</span>
                  ) : (
                    (role.permissions || []).map((p) => (
                      <span
                        key={p}
                        className="inline-flex rounded-md bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-800"
                      >
                        {PERMISSION_CATALOG.find((c) => c.id === p)?.label || p.replace(/_/g, ' ')}
                      </span>
                    ))
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {tab === 'users' && (
        <>
          <div className="card p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search users by name, email, or role..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input pl-10 w-full"
              />
            </div>
          </div>

          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Current Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Permissions
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map((user) => (
                    <UserRow
                      key={user.id}
                      user={user}
                      roleOptions={roleOptions}
                      roles={roles}
                      isEditing={editingUser === user.id}
                      onEdit={() => setEditingUser(user.id)}
                      onCancel={() => setEditingUser(null)}
                      onSave={(newRole, newPermissions) =>
                        updateUserRole(user.id, newRole, newPermissions)
                      }
                      canEdit={roleService.canUserManageRole(userProfile.role, user.role)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {showRoleForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-border bg-background shadow-xl">
            <form onSubmit={saveRole} className="space-y-4 p-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">
                  {editingRole ? 'Edit role' : 'Create role'}
                </h2>
                <button type="button" onClick={() => setShowRoleForm(false)} className="p-1 text-muted-foreground">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Role name</label>
                <input
                  required
                  value={roleForm.name}
                  onChange={(e) => setRoleForm((p) => ({ ...p, name: e.target.value }))}
                  className="form-input w-full"
                  placeholder="e.g. Cashier"
                  disabled={Boolean(editingRole?.isSystem)}
                />
                {editingRole?.isSystem && (
                  <p className="text-xs text-muted-foreground">
                    System role name is locked; you can still change permissions.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Description</label>
                <input
                  value={roleForm.description}
                  onChange={(e) => setRoleForm((p) => ({ ...p, description: e.target.value }))}
                  className="form-input w-full"
                  placeholder="Optional"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Hierarchy level (1–4)</label>
                <select
                  value={roleForm.hierarchyLevel}
                  onChange={(e) =>
                    setRoleForm((p) => ({ ...p, hierarchyLevel: Number(e.target.value) }))
                  }
                  className="form-input w-full"
                  disabled={Boolean(editingRole?.isSystem)}
                >
                  <option value={1}>1 — Customer-like</option>
                  <option value={2}>2 — Staff-like</option>
                  <option value={3}>3 — Manager-like</option>
                  <option value={4}>4 — Owner-like</option>
                </select>
              </div>

              <div className="space-y-3">
                <p className="text-sm font-medium">Access permissions</p>
                {permissionGroups.map((group) => (
                  <div key={group} className="rounded-lg border border-border/70 p-3">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {group}
                    </p>
                    <div className="space-y-2">
                      {PERMISSION_CATALOG.filter((p) => p.group === group).map((perm) => (
                        <label key={perm.id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={roleForm.permissions.includes(perm.id)}
                            onChange={() => toggleFormPermission(perm.id)}
                            className="rounded border-gray-300 text-primary focus:ring-primary"
                          />
                          <span>{perm.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="submit"
                  disabled={savingRole}
                  className="flex-1 rounded-xl bg-primary py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {savingRole ? 'Saving…' : editingRole ? 'Save changes' : 'Create role'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowRoleForm(false)}
                  className="flex-1 rounded-xl border border-border py-2.5 text-sm font-semibold"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

function UserRow({ user, isEditing, onEdit, onCancel, onSave, canEdit, roleOptions, roles }) {
  const [selectedRole, setSelectedRole] = useState(user.role)
  const [selectedPermissions, setSelectedPermissions] = useState(
    Array.isArray(user.permissions) ? user.permissions : []
  )

  useEffect(() => {
    if (!isEditing) return
    setSelectedRole(user.role)
    setSelectedPermissions(Array.isArray(user.permissions) ? user.permissions : [])
  }, [isEditing, user.role, user.permissions])

  function handleRoleChange(roleKey) {
    setSelectedRole(roleKey)
    const roleDoc = (roles || []).find((r) => (r.key || r.id) === roleKey)
    if (roleDoc?.permissions) {
      setSelectedPermissions([...roleDoc.permissions])
    }
  }

  function handlePermissionChange(permission, checked) {
    if (checked) {
      setSelectedPermissions([...selectedPermissions, permission])
    } else {
      setSelectedPermissions(selectedPermissions.filter((p) => p !== permission))
    }
  }

  return (
    <tr>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="h-10 w-10 rounded-full bg-primary-600 flex items-center justify-center">
            <span className="text-sm font-medium text-white">
              {user.name?.charAt(0) || user.displayName?.charAt(0) || user.email?.charAt(0) || 'U'}
            </span>
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900">
              {user.name || user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown'}
            </div>
            <div className="text-sm text-gray-500">{user.email}</div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {isEditing ? (
          <CustomDropdown
            options={roleOptions}
            value={selectedRole}
            onChange={handleRoleChange}
            placeholder="Select Role"
            className="text-sm"
          />
        ) : (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 capitalize">
            <Shield className="h-3 w-3 mr-1" />
            {user.role}
          </span>
        )}
      </td>
      <td className="px-6 py-4">
        {isEditing ? (
          <div className="space-y-2 max-w-xs max-h-48 overflow-y-auto">
            {PERMISSION_CATALOG.map((permission) => (
              <label key={permission.id} className="flex items-center text-sm">
                <input
                  type="checkbox"
                  checked={selectedPermissions.includes(permission.id)}
                  onChange={(e) => handlePermissionChange(permission.id, e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-gray-700">{permission.label}</span>
              </label>
            ))}
          </div>
        ) : (
          <div className="flex flex-wrap gap-1">
            {Array.isArray(user.permissions) && user.permissions.length > 0 ? (
              <>
                {user.permissions.slice(0, 3).map((permission) => (
                  <span
                    key={permission}
                    className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-100 text-blue-800"
                  >
                    {permission.replace(/_/g, ' ')}
                  </span>
                ))}
                {user.permissions.length > 3 && (
                  <span className="text-xs text-gray-500">+{user.permissions.length - 3} more</span>
                )}
              </>
            ) : (
              <span className="text-xs text-gray-500">No permissions set</span>
            )}
          </div>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
        {canEdit && (
          <div className="flex space-x-2">
            {isEditing ? (
              <>
                <button
                  type="button"
                  onClick={() => onSave(selectedRole, selectedPermissions)}
                  className="text-green-600 hover:text-green-900"
                >
                  <Save className="h-4 w-4" />
                </button>
                <button type="button" onClick={onCancel} className="text-gray-600 hover:text-gray-900">
                  <X className="h-4 w-4" />
                </button>
              </>
            ) : (
              <button type="button" onClick={onEdit} className="text-primary-600 hover:text-primary-900">
                <Edit className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      </td>
    </tr>
  )
}
